import styled, { css, keyframes } from "styled-components";

const theme = {
  bg: "#1C1E2A",
  surface: "#252836",
  surfaceHover: "#2d3040",
  primary: "#50fa7b",
  secondary: "#8be9fd",
  accent: "#ff79c6",
  text: "#f8f8f2",
  textSecondary: "#bd93f9",
  border: "#44475a",
  danger: "#ff5555",
  warning: "#f1fa8c",
  // Paleta Golden Premium
  goldDark: "#B8860B",
  goldBase: "#D4AF37",
  goldBright: "#FFD700",
  whiteGlow: "#FFFFFF",
};

// --- ANIMAÇÕES SINCRONIZADAS PARA EFEITO SNAKE (8 segundos total) ---

// Topo: Move da Esquerda para Direita
const slideTop = keyframes`
    0% { background-position: -100% 0; }
    25%, 100% { background-position: 100% 0; }
`;

// Direita: Move de Cima para Baixo
const slideRight = keyframes`
    0%, 25% { background-position: 100% -100%; }
    50%, 100% { background-position: 100% 100%; }
`;

// Baixo: Move da Direita para Esquerda
const slideBottom = keyframes`
    0%, 50% { background-position: 100% 100%; }
    75%, 100% { background-position: -100% 100%; }
`;

// Esquerda: Move de Baixo para Cima
const slideLeft = keyframes`
    0%, 75% { background-position: 0 100%; }
    100% { background-position: 0 -100%; }
`;

// Pulso suave para o fundo da linha
const goldenPulse = keyframes`
  0%, 100% { background-color: rgba(212, 175, 55, 0.05); box-shadow: inset 0 0 10px rgba(212, 175, 55, 0.1); }
  50% { background-color: rgba(212, 175, 55, 0.12); box-shadow: inset 0 0 25px rgba(212, 175, 55, 0.3); }
`;

export const TabelaContainer = styled.div`
  background-color: ${theme.surface};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow-x: auto;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${theme.bg};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${theme.goldBase};
    border-radius: 10px;
    border: 2px solid ${theme.bg};
  }
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse; /* Importante para o efeito funcionar bem nas tr */
  color: ${theme.text};
  min-width: 1200px;
  border-spacing: 0;
`;

export const TabelaHead = styled.thead`
  background-color: rgba(0, 0, 0, 0.3);
`;

export const TabelaHeader = styled.th`
  padding: 1.2rem 1.5rem;
  text-align: left;
  font-weight: 700;
  font-size: 0.85rem;
  color: ${theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1px solid ${theme.border};
  white-space: nowrap;
`;

export const TabelaBody = styled.tbody``;

export const TabelaRow = styled.tr`
  border-bottom: 1px solid ${theme.border};
  transition: all 0.3s ease;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  /* --- EFEITO GOLDEN BOX ATIVADA (MODO PREMIUM) --- */
  ${(props) =>
    props.isGoldenBoxActived &&
    css`
      /* Animação de fundo pulsante */
      animation: ${goldenPulse} 4s ease-in-out infinite;

      /* Remove bordas padrão para não atrapalhar o efeito */
      border-left: none !important;
      border-bottom: 1px solid rgba(212, 175, 55, 0.2);

      /* Definição dos 4 feixes de luz (Gradientes) */
      /* Ordem: Top, Right, Bottom, Left */
      background-image: linear-gradient(
          90deg,
          transparent,
          ${theme.goldBase},
          ${theme.goldBright},
          ${theme.whiteGlow}
        ),
        linear-gradient(
          180deg,
          transparent,
          ${theme.goldBase},
          ${theme.goldBright},
          ${theme.whiteGlow}
        ),
        linear-gradient(
          270deg,
          transparent,
          ${theme.goldBase},
          ${theme.goldBright},
          ${theme.whiteGlow}
        ),
        linear-gradient(
          0deg,
          transparent,
          ${theme.goldBase},
          ${theme.goldBright},
          ${theme.whiteGlow}
        );

      background-repeat: no-repeat;

      /* Tamanho da linha (espessura) */
      /* Top/Bottom: 100% width, 2px height */
      /* Left/Right: 2px width, 100% height */
      background-size: 100% 2px, 2px 100%, 100% 2px, 2px 100%;

      /* Posicionamento inicial (Fora da visão) */
      background-position: -100% 0, 100% -100%, 100% 100%, 0 100%;

      /* Aplicação das 4 animações simultâneas para criar o loop */
      /* O animation-delay cria a sequência perfeita */
      /* 8s total: 2s para cada lado */
      /* animation-name duration timing-function delay iteration-count */
      animation: ${goldenPulse} 4s ease-in-out infinite,
        ${slideTop} 8s linear infinite, ${slideRight} 8s linear infinite 2s,
        /* Delay de 2s para começar após o topo */ ${slideBottom} 8s linear
          infinite 4s,
        /* Delay de 4s */ ${slideLeft} 8s linear infinite 6s; /* Delay de 6s */
    `}

  ${(props) =>
    props.isClickable &&
    css`
      cursor: pointer;
      &:hover {
        background-color: ${theme.surfaceHover};
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

        ${props.isGoldenBoxActived &&
        css`
          /* Intensifica o brilho no hover para a Golden */
          box-shadow: inset 0 0 30px rgba(212, 175, 55, 0.4),
            0 5px 15px rgba(0, 0, 0, 0.3);
          z-index: 2; /* Traz para frente */
        `}
      }
    `}
`;

export const TabelaData = styled.td`
  padding: 1rem 1.5rem;
  text-align: left;
  vertical-align: middle;
  font-size: 0.9rem;
  white-space: nowrap;

  /* Se a linha for Golden, deixa o texto dourado brilhante */
  ${(props) =>
    props.isGoldenBoxActived &&
    css`
      color: #ffd700 !important;
      font-weight: 600;
      text-shadow: 0 0 10px rgba(212, 175, 55, 0.3);

      /* Ícones dentro da célula também ficam dourados */
      svg {
        color: #ffd700 !important;
        filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.5));
      }
    `}
`;

export const HighlightText = styled.span`
  color: ${theme.primary};
  font-weight: 700;

  /* Se estiver dentro de uma linha Golden, muda para branco brilhante */
  ${TabelaRow}[isGoldenBoxActived] & {
    color: #fff;
    text-shadow: 0 0 5px ${theme.primary};
  }
`;

export const StatusBadge = styled.span`
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

  background-color: ${({ status }) => {
    switch (status) {
      case 1:
        return theme.primary;
      case 3:
        return theme.danger;
      case 4:
        return theme.warning;
      case 5:
        return theme.textSecondary;
      default:
        return theme.border;
    }
  }};

  color: ${({ status }) => (status === 4 ? "#1C1E2A" : "#fff")};
`;

export const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${theme.secondary};
  cursor: pointer;
  font-size: 1.1rem;
  width: 35px;
  height: 35px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.secondary};
    color: #fff;
    transform: scale(1.1);
    box-shadow: 0 0 10px ${theme.secondary};
    border-color: ${theme.secondary};
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  gap: 0.8rem;
  background-color: ${theme.surface};
  border-top: 1px solid ${theme.border};
  border-radius: 0 0 12px 12px;
  flex-wrap: wrap;
`;

export const PaginationButton = styled.button`
  background-color: ${theme.bg};
  color: ${theme.text};
  border: 1px solid ${theme.border};
  border-radius: 8px;
  cursor: pointer;
  height: 40px;
  width: 40px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${theme.goldBase};
    color: white;
    border-color: ${theme.goldBright};
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const PageInfo = styled.span`
  color: ${theme.textSecondary};
  font-size: 0.9rem;
  margin: 0 1rem;
  font-weight: 600;
`;

export const PageInput = styled.input`
  height: 40px;
  width: 80px;
  background-color: ${theme.bg};
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.text};
  text-align: center;
  font-size: 0.9rem;
  margin: 0 0.5rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.goldBase};
    box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
  }
`;

export const Filters = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  margin-top: 1.5rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

export const FilterBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex-grow: 1;

  .name {
    font-size: 0.75rem;
    font-weight: 700;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-left: 4px;
  }

  .input-box {
    width: 100%;
    min-width: 220px;
    height: 48px;
    background-color: ${theme.surface};
    border: 1px solid ${theme.border};
    border-radius: 12px;
    color: ${theme.text};
    font-size: 0.95rem;
    padding: 0 1.2rem;
    box-sizing: border-box;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${theme.goldBase};
      box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
      background-color: ${theme.surfaceHover};
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }

    &[as="select"] {
      cursor: pointer;
    }
  }
`;

// Adicione isso ao final do arquivo TabelaContratosStyle.js

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7); /* Fundo escuro transparente */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

export const ModalContent = styled.div`
  background-color: #fff;
  width: 90%;
  max-width: 900px;
  height: 90vh; /* Ocupa 90% da altura da tela */
  border-radius: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
  border-radius: 10px 10px 0 0;

  h2 {
    margin: 0;
    font-size: 18px;
    color: #333;
    font-weight: 700;
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto; /* Permite rolar o contrato dentro do modal */
  padding: 20px;
  background-color: #f4f4f4;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  transition: color 0.2s;

  &:hover {
    color: #ff5555;
  }
`;

export const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${theme.goldBase};
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    background-color: ${theme.goldDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    span {
      display: none; /* Esconde o texto em telas pequenas, mantém só o ícone */
    }
    padding: 8px;
  }
`;